import {throttle} from 'lodash';

export default class Parallax {
    constructor() {
        const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        if (mediaQuery && mediaQuery.matches) {
            return;
        }
        this.parallaxElements = document.querySelectorAll('.parallax');
        this.parallaxElementsTop = document.querySelectorAll('.parallax--top');
        this.parallaxElementsMiddle = document.querySelectorAll('.parallax:not(.parallax--top)');
        this.sentinelCount = 0;
        this.ioSupport =
            'IntersectionObserver' in window &&
            'IntersectionObserverEntry' in window &&
            'intersectionRatio' in window.IntersectionObserverEntry.prototype;

        this.init();
    }

    initializeIo(parallax) {
        parallax.parallaxElement = parallax.querySelector('.parallax__element:not(.parallax--no-image)');
        parallax.isTop = parallax.classList.contains('parallax--top');
        parallax.rect = parallax.getBoundingClientRect();
        parallax.parallaxElementHeight = parallax.rect.height;
        parallax.reduceScrollingEffectBy = parallax.dataset?.reduceScrollingEffectBy
            ? 1 - parseInt(parallax.dataset.reduceScrollingEffectBy) * 0.01
            : 1;
        this.createSentinel(parallax);
        parallax.yPosition = -1;
        const thresholdArray = steps => [...Array(steps + 1)].map((_, index) => index / steps || 0);
        const observerConfig = {
            threshold: thresholdArray(300), // Number of steps
        };
        parallax.observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                this.ioHandler(parallax, entry);
            });
        }, observerConfig);
        parallax.observer.POLL_INTERVAL = 20;
        parallax.observer.observe(parallax.sentinel);
    }

    createSentinel(parallax) {
        parallax.sentinel = document.createElement('div');
        parallax.sentinel.className = 'parallax__sentinel';
        parallax.sentinel.style.left = `${this.sentinelCount}px`;
        this.configureSentinel(parallax);
        document.body.appendChild(parallax.sentinel);
        this.sentinelCount += 1;
    }

    configureSentinel(parallax) {
        const bodyRect = document.body.getBoundingClientRect();
        parallax.rect = parallax.getBoundingClientRect();
        const sentinelTop = parallax.rect.top - bodyRect.top + parallax.rect.height / 2 - window.innerHeight / 2; //eslint-disable-line
        if (parallax.rect.height >= window.innerHeight) {
            requestAnimationFrame(() => {
                parallax.sentinel.style.height = `${window.innerHeight}px`;
                parallax.sentinel.style.bottom = 'unset';
                parallax.sentinel.style.top = `${parallax.rect.top - bodyRect.top}px`;
            });
        } else if (bodyRect.height > sentinelTop + window.innerHeight) {
            requestAnimationFrame(() => {
                parallax.sentinel.style.height = `${window.innerHeight}px`;
                parallax.sentinel.style.bottom = 'unset';
                parallax.sentinel.style.top = `${sentinelTop}px`;
            });
        } else {
            requestAnimationFrame(() => {
                parallax.sentinel.style.height = 'unset';
                parallax.sentinel.style.bottom = '0';
                parallax.sentinel.style.top = `${sentinelTop}px`;
            });
        }
    }

    ioHandler(parallax, entry) {
        const isAbove = entry.boundingClientRect.y < entry.rootBounds.y;
        const useful =
            (entry.boundingClientRect.height - parallax.parallaxElementHeight) / (2 * entry.boundingClientRect.height); //eslint-disable-line
        let yPosition = (1 - (1 - entry.intersectionRatio) / (1 - useful)) / 0.02;
        const yPositionPercent = isAbove ? 100 - yPosition : yPosition;
        yPosition = (yPositionPercent / 3).toFixed(4) * parallax.reduceScrollingEffectBy;
        let top =
            (entry.boundingClientRect.height + parallax.parallaxElementHeight) / 2 / entry.boundingClientRect.height; //eslint-disable-line
        if (top > 1) {
            top = 1;
        }
        if (entry.intersectionRatio > useful && parallax.parallaxElement && yPosition !== parallax.yPosition) {
            if (parallax.isTop) {
                const topPosition =
                    (1 - (entry.intersectionRatio - useful) / (top - useful)) * 33 * parallax.reduceScrollingEffectBy;
                parallax.parallaxElement.style.transform = `translate3d(0, ${topPosition}% ,0)`;
            } else {
                parallax.parallaxElement.style.transform = `translate3d(0, ${yPosition}% ,0)`;
            }
        }
        parallax.yPosition = yPosition;
    }

    elementHeightUpdate() {
        this.parallaxElements.forEach(parallax => {
            parallax.parallaxElementHeight = parallax.getBoundingClientRect().height;
            this.configureSentinel(parallax);
        });
    }

    init() {
        if (!this.ioSupport) return;

        this.parallaxElements.forEach(parallax => {
            this.initializeIo(parallax);
        });

        window.addEventListener(
            'resize',
            throttle(() => {
                this.elementHeightUpdate();
            }, 32),
            {passive: true}
        );
    }
}
